import { MESSAGE_IDS } from '@trust_network/shared/message-ids';
import { frontendConfig } from '../config/env';
import { ApiError, ApiErrorPayload } from './model/error';

const API_URL = frontendConfig.apiUrl;
const API_TIMEOUT_MS = 10_000;

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

type ApiSuccessParser<T> = (data: unknown) => T;

export { ApiError };
export type { ApiErrorPayload };

export async function apiRequest<T>(
  path: string,
  options: {
    method?: HttpMethod;
    token?: string | null;
    body?: unknown;
    parse: ApiSuccessParser<T>;
  }
) {
  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => {
    controller.abort();
  }, API_TIMEOUT_MS);

  let response: Response;

  try {
    response = await fetch(`${API_URL}${path}`, {
      method: options.method ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw new ApiError(MESSAGE_IDS.requestFailed, undefined, {
        transport: {
          statusText: `Request timed out after ${API_TIMEOUT_MS}ms`,
        },
      });
    }

    throw error;
  } finally {
    globalThis.clearTimeout(timeoutId);
  }

  const rawBody = await response.text();
  const data = rawBody ? tryParseJson(rawBody) : null;

  if (!response.ok) {
    if (!data) {
      throw new ApiError(MESSAGE_IDS.requestFailed, response.status, {
        statusCode: response.status,
        transport: {
          statusText: response.statusText,
          contentType: response.headers.get('content-type') ?? undefined,
          bodyPreview: rawBody.slice(0, 200) || undefined,
        },
      });
    }

    throw new ApiError(
      data?.messageId ?? data?.message ?? MESSAGE_IDS.requestFailed,
      response.status,
      data ?? undefined
    );
  }

  return options.parse(data);
}

function tryParseJson(value: string) {
  try {
    return JSON.parse(value) as ApiErrorPayload;
  } catch {
    return null;
  }
}

function isAbortError(error: unknown) {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';
}
